import {QEPInput} from '@qep/shared/src/components/QEPInput/qep-input';
import {type TradableSymbol} from '@qep/types/src';
import React, {
  type ChangeEvent, useEffect, useRef, useState,
} from 'react';

type AccountSelectorDropdownProps = {
  tradableSymbol: TradableSymbol;
  handleChange: (value: number, sym: TradableSymbol) => void;
};

/** Renders a default-quantity control that preserves accepted input until blur resolves the edit. */
const DefaultQtySetter: React.FC<AccountSelectorDropdownProps> = (props: AccountSelectorDropdownProps) => {
  const {tradableSymbol: symbol} = props;

  const [qty, setQty] = useState<string>(symbol.defaultQty ? String(symbol.defaultQty) : '0');
  const hasUnsavedEdit = useRef(false);
  const hasInvalidEdit = useRef(false);

  /** Applies authoritative API updates when no edit is pending so the field does not retain a stale value. */
  useEffect(() => {
    if (!hasUnsavedEdit.current) {
      setQty(symbol.defaultQty ? String(symbol.defaultQty) : '0');
    }
  }, [symbol.defaultQty, symbol.rootSymbol, symbol.segmentMic]);

  /** Stores a valid non-negative edit and marks it dirty so authoritative updates cannot overwrite it. */
  const _handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newQty = Number(event.target.value);
    if (Number.isNaN(newQty) || newQty < 0) {
      hasInvalidEdit.current = true;
      return;
    }

    hasInvalidEdit.current = false;
    hasUnsavedEdit.current = true;
    setQty(event.target.value);
  };

  /** Ends the edit lifecycle, restoring rejected edits or committing an accepted quantity on blur. */
  const setDefaultQty = (event: ChangeEvent<HTMLInputElement>) => {
    const hasAcceptedEdit = hasUnsavedEdit.current;
    hasUnsavedEdit.current = false;
    if (hasInvalidEdit.current) {
      hasInvalidEdit.current = false;
      setQty(symbol.defaultQty ? String(symbol.defaultQty) : '0');
      return;
    }

    if (!hasAcceptedEdit) {
      return;
    }

    const inputValue = event.target.value;
    const numberValue = Number(inputValue);

    if (inputValue && !Number.isNaN(numberValue) && numberValue > 0) {
      setQty(inputValue);
      props.handleChange(numberValue, symbol);
    } else {
      setQty('0');
      props.handleChange(0, symbol);
    }
  };

  return (
    <QEPInput
      className={`qty-input ${Number.isNaN(Number(qty)) ? 'error' : ''}`}
      onBlur={setDefaultQty}
      onChange={_handleChange}
      pattern='[0-9]*'
      type='number'
      value={qty}
    />
  );
};

export default DefaultQtySetter;
